import { supabase } from './supabaseClient';
import { ErrorManager } from '../utils/errorManager';
import { ErrorFactory } from '../utils/errorFactory';
import { ErrorLogger } from '../utils/errorLogger';

// Cache interface and implementation
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class ProjectCreationCache {
  private static cache: Map<string, CacheEntry<any>> = new Map();
  private static readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private static readonly SETTINGS_TTL = 10 * 60 * 1000; // 10 minutes for settings
  private static readonly ELIGIBILITY_TTL = 2 * 60 * 1000; // 2 minutes for eligibility
  private static readonly USER_POINTS_TTL = 1 * 60 * 1000; // 1 minute for user points

  static set<T>(key: string, data: T, customTtl?: number): void {
    const ttl = customTtl || this.DEFAULT_TTL;
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  static get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  static invalidate(key: string): void {
    this.cache.delete(key);
  }

  static invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
  }

  static clear(): void {
    this.cache.clear();
  }

  static getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  // Specific cache key generators
  static getSettingsKey(): string {
    return 'project_creation_settings';
  }

  static getEligibilityKey(userId: string): string {
    return `eligibility_${userId}`;
  }

  static getUserPointsKey(userId: string): string {
    return `user_points_${userId}`;
  }

  static getLinkedInAuthKey(userId: string): string {
    return `linkedin_auth_${userId}`;
  }

  // TTL getters
  static get SETTINGS_CACHE_TTL(): number {
    return this.SETTINGS_TTL;
  }

  static get ELIGIBILITY_CACHE_TTL(): number {
    return this.ELIGIBILITY_TTL;
  }

  static get USER_POINTS_CACHE_TTL(): number {
    return this.USER_POINTS_TTL;
  }
}

// Memoization decorator for methods
function memoize<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  getCacheKey: (...args: Parameters<T>) => string,
  ttl?: number
): T {
  return (async (...args: Parameters<T>) => {
    const cacheKey = getCacheKey(...args);
    const cached = ProjectCreationCache.get(cacheKey);
    
    if (cached !== null) {
      return cached;
    }

    const result = await fn(...args);
    ProjectCreationCache.set(cacheKey, result, ttl);
    return result;
  }) as T;
}

// Real-time subscription manager
class ProjectCreationRealtimeManager {
  private static subscriptions: Map<string, any> = new Map();
  private static callbacks: Map<string, Set<() => void>> = new Map();

  static subscribe(event: 'settings_changed' | 'user_points_changed', callback: () => void): () => void {
    if (!this.callbacks.has(event)) {
      this.callbacks.set(event, new Set());
    }
    
    this.callbacks.get(event)!.add(callback);

    // Set up Supabase subscription if not already done
    this.setupSubscription(event);

    // Return unsubscribe function
    return () => {
      this.callbacks.get(event)?.delete(callback);
      if (this.callbacks.get(event)?.size === 0) {
        this.cleanupSubscription(event);
      }
    };
  }

  private static setupSubscription(event: string): void {
    if (this.subscriptions.has(event)) {
      return; // Already subscribed
    }

    let subscription;

    switch (event) {
      case 'settings_changed':
        subscription = supabase
          .channel('project_creation_settings_changes')
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'project_creation_settings'
          }, () => {
            // Invalidate settings cache
            ProjectCreationCache.invalidate(ProjectCreationCache.getSettingsKey());
            // Invalidate all eligibility caches since settings affect eligibility
            ProjectCreationCache.invalidatePattern('^eligibility_');
            // Notify callbacks
            this.notifyCallbacks('settings_changed');
          })
          .subscribe();
        break;

      case 'user_points_changed':
        subscription = supabase
          .channel('user_points_changes')
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'users',
            filter: 'points=neq.null'
          }, (payload) => {
            const userId = payload.new?.id;
            if (userId) {
              // Invalidate specific user caches
              ProjectCreationCache.invalidate(ProjectCreationCache.getUserPointsKey(userId));
              ProjectCreationCache.invalidate(ProjectCreationCache.getEligibilityKey(userId));
              // Notify callbacks
              this.notifyCallbacks('user_points_changed');
            }
          })
          .subscribe();
        break;
    }

    if (subscription) {
      this.subscriptions.set(event, subscription);
    }
  }

  private static cleanupSubscription(event: string): void {
    const subscription = this.subscriptions.get(event);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(event);
    }
  }

  private static notifyCallbacks(event: string): void {
    const callbacks = this.callbacks.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error(`Error in realtime callback for ${event}:`, error);
        }
      });
    }
  }

  static cleanup(): void {
    // Cleanup all subscriptions
    for (const [event, subscription] of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions.clear();
    this.callbacks.clear();
  }
}

// Types for project creation settings
export interface ProjectCreationSettings {
  minimum_points_required: {
    value: number;
    enabled: boolean;
  };
  linkedin_auth_required: {
    enabled: boolean;
  };
}

export interface ProjectCreationEligibility {
  canCreate: boolean;
  reasons: {
    insufficientPoints?: {
      required: number;
      current: number;
    };
    linkedinRequired?: boolean;
  };
}

// Error types for project creation
export enum ProjectCreationErrorType {
  INSUFFICIENT_POINTS = 'insufficient_points',
  LINKEDIN_REQUIRED = 'linkedin_required',
  SETTINGS_LOAD_ERROR = 'settings_load_error',
  ELIGIBILITY_CHECK_ERROR = 'eligibility_check_error',
  SETTINGS_UPDATE_ERROR = 'settings_update_error',
  VALIDATION_ERROR = 'validation_error'
}

export interface ProjectCreationError extends Error {
  type: ProjectCreationErrorType;
  details?: any;
  recoverable: boolean;
}

// Validation functions
const validatePointsRequirement = (value: number): boolean => {
  return Number.isInteger(value) && value >= 0 && value <= 10000;
};

const validateSettingsUpdate = (settingName: string, value: any): boolean => {
  switch (settingName) {
    case 'minimum_points_required':
      return typeof value === 'object' && 
             typeof value.enabled === 'boolean' &&
             typeof value.value === 'number' &&
             validatePointsRequirement(value.value);
    case 'linkedin_auth_required':
      return typeof value === 'object' && 
             typeof value.enabled === 'boolean';
    default:
      return false;
  }
};

export class ProjectCreationSettingsService {
  /**
   * Get all project creation settings with caching
   */
  static async getSettings(): Promise<ProjectCreationSettings> {
    const cacheKey = ProjectCreationCache.getSettingsKey();
    const cached = ProjectCreationCache.get<ProjectCreationSettings>(cacheKey);
    
    if (cached) {
      return cached;
    }

    try {
      const settings = await ErrorManager.withRetry(
        () => this._fetchSettingsFromDatabase(),
        {
          operationName: 'fetch_project_creation_settings',
          maxAttempts: 3,
          baseDelay: 1000,
          exponentialBackoff: true
        }
      );
      ProjectCreationCache.set(cacheKey, settings, ProjectCreationCache.SETTINGS_CACHE_TTL);
      return settings;
    } catch (error) {
      const handlingResult = await ErrorManager.handleError(error, {
        operation: 'get_project_creation_settings'
      });
      
      if (!handlingResult.success) {
        // Return default settings as graceful degradation
        const defaultSettings: ProjectCreationSettings = {
          minimum_points_required: { value: 0, enabled: false },
          linkedin_auth_required: { enabled: false }
        };
        ErrorLogger.logUserAction('SETTINGS_FALLBACK', 'get_project_creation_settings', 'fallback_used');
        return defaultSettings;
      }
      
      throw error;
    }
  }

  /**
   * Internal method to fetch settings from database (not cached)
   */
  private static async _fetchSettingsFromDatabase(): Promise<ProjectCreationSettings> {
    const { data, error } = await supabase
      .from('project_creation_settings')
      .select('setting_name, setting_value')
      .eq('is_active', true);

    if (error) {
      throw ErrorFactory.fromSupabaseError(error, 'fetch_project_creation_settings', {
        additionalData: { operation: 'fetch_settings' }
      });
    }

    if (!data || data.length === 0) {
      // Return default settings if none exist
      return {
        minimum_points_required: {
          value: 0,
          enabled: false
        },
        linkedin_auth_required: {
          enabled: false
        }
      };
    }

    // Transform array to object structure
    const settings = data.reduce((acc, setting) => {
      acc[setting.setting_name as keyof ProjectCreationSettings] = setting.setting_value;
      return acc;
    }, {} as ProjectCreationSettings);

    // Ensure all required settings exist with defaults
    const defaultSettings: ProjectCreationSettings = {
      minimum_points_required: {
        value: 0,
        enabled: false
      },
      linkedin_auth_required: {
        enabled: false
      }
    };

    return {
      ...defaultSettings,
      ...settings
    };
  }

  /**
   * Update a specific setting and invalidate cache
   */
  static async updateSetting(settingName: string, value: any): Promise<void> {
    try {
      // Validate the setting update
      if (!validateSettingsUpdate(settingName, value)) {
        throw ErrorFactory.createValidationError(
          'INVALID_SETTING_VALUE',
          `Invalid setting value for ${settingName}`,
          `The provided value for setting '${settingName}' does not meet validation requirements`,
          settingName,
          'Setting validation rules',
          'Valid setting structure',
          value
        );
      }

      const operation = async () => {
        const { error } = await supabase
          .from('project_creation_settings')
          .upsert({
            setting_name: settingName,
            setting_value: value,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'setting_name'
          });

        if (error) {
          throw ErrorFactory.fromSupabaseError(error, 'update_project_creation_setting', {
            additionalData: { settingName, value }
          });
        }
      };

      await ErrorManager.withRetry(operation, {
        operationName: 'update_project_creation_setting',
        maxAttempts: 3,
        baseDelay: 1000
      });

      // Invalidate relevant caches after successful update
      ProjectCreationCache.invalidate(ProjectCreationCache.getSettingsKey());
      // Invalidate all eligibility caches since settings affect eligibility
      ProjectCreationCache.invalidatePattern('^eligibility_');

      // Log successful setting update
      ErrorLogger.logUserAction('SETTINGS_UPDATE', 'update_project_creation_setting', settingName);
    } catch (error) {
      // Re-throw validation errors and other app errors
      if (error && typeof error === 'object' && 'category' in error) {
        throw error;
      }
      
      // Handle unexpected errors
      throw ErrorFactory.fromUnknown(
        error,
        'SETTINGS_UPDATE_ERROR',
        'An unexpected error occurred while updating settings'
      );
    }
  }

  /**
   * Check if a user is eligible to create projects with caching and memoization
   */
  static async checkEligibility(userId: string): Promise<ProjectCreationEligibility> {
    const cacheKey = ProjectCreationCache.getEligibilityKey(userId);
    const cached = ProjectCreationCache.get<ProjectCreationEligibility>(cacheKey);
    
    if (cached) {
      return cached;
    }

    try {
      const eligibility = await ErrorManager.withRetry(
        () => this._calculateEligibility(userId),
        {
          operationName: 'check_project_creation_eligibility',
          maxAttempts: 2,
          baseDelay: 500
        }
      );
      ProjectCreationCache.set(cacheKey, eligibility, ProjectCreationCache.ELIGIBILITY_CACHE_TTL);
      return eligibility;
    } catch (error) {
      // Re-throw app errors
      if (error && typeof error === 'object' && 'category' in error) {
        throw error;
      }
      
      throw ErrorFactory.fromUnknown(
        error,
        'ELIGIBILITY_CHECK_ERROR',
        'Unable to check project creation eligibility'
      );
    }
  }

  /**
   * Internal method to calculate eligibility (not cached)
   */
  private static async _calculateEligibility(userId: string): Promise<ProjectCreationEligibility> {
    const settings = await this.getSettings();
    const eligibility: ProjectCreationEligibility = {
      canCreate: true,
      reasons: {}
    };

    // Check points requirement
    if (settings.minimum_points_required.enabled) {
      const userPoints = await this.getUserPoints(userId);
      
      if (userPoints < settings.minimum_points_required.value) {
        eligibility.canCreate = false;
        eligibility.reasons.insufficientPoints = {
          required: settings.minimum_points_required.value,
          current: userPoints
        };
      }
    }

    // Check LinkedIn requirement
    if (settings.linkedin_auth_required.enabled) {
      const hasLinkedIn = await this.hasLinkedInAuth(userId);
      if (!hasLinkedIn) {
        eligibility.canCreate = false;
        eligibility.reasons.linkedinRequired = true;
      }
    }

    return eligibility;
  }

  /**
   * Check if a user has LinkedIn authentication with caching
   */
  static async hasLinkedInAuth(userId: string): Promise<boolean> {
    const cacheKey = ProjectCreationCache.getLinkedInAuthKey(userId);
    const cached = ProjectCreationCache.get<boolean>(cacheKey);
    
    if (cached !== null) {
      return cached;
    }

    try {
      const { data, error } = await supabase.rpc('check_linkedin_auth', {
        user_id: userId
      });

      if (error) {
        console.error('Error checking LinkedIn auth:', error);
        // Return false instead of throwing to be more resilient
        return false;
      }

      const hasAuth = data || false;
      // Cache LinkedIn auth status for 5 minutes
      ProjectCreationCache.set(cacheKey, hasAuth, 5 * 60 * 1000);
      return hasAuth;
    } catch (error) {
      console.error('Unexpected error checking LinkedIn auth:', error);
      // Return false instead of throwing to be more resilient
      return false;
    }
  }

  /**
   * Get user's current points with caching
   */
  static async getUserPoints(userId: string): Promise<number> {
    const cacheKey = ProjectCreationCache.getUserPointsKey(userId);
    const cached = ProjectCreationCache.get<number>(cacheKey);
    
    if (cached !== null) {
      return cached;
    }

    try {
      const operation = async () => {
        const { data, error } = await supabase
          .from('users')
          .select('points')
          .eq('id', userId)
          .single();

        if (error) {
          throw ErrorFactory.fromSupabaseError(error, 'get_user_points', {
            additionalData: { userId }
          });
        }

        return data?.points || 0;
      };

      const points = await ErrorManager.withRetry(operation, {
        operationName: 'get_user_points',
        maxAttempts: 3,
        baseDelay: 500
      });

      ProjectCreationCache.set(cacheKey, points, ProjectCreationCache.USER_POINTS_CACHE_TTL);
      return points;
    } catch (error) {
      // Re-throw app errors
      if (error && typeof error === 'object' && 'category' in error) {
        throw error;
      }
      
      throw ErrorFactory.fromUnknown(
        error,
        'USER_POINTS_ERROR',
        'Unable to retrieve user points'
      );
    }
  }

  /**
   * Invalidate cache for a specific user
   */
  static invalidateUserCache(userId: string): void {
    ProjectCreationCache.invalidate(ProjectCreationCache.getEligibilityKey(userId));
    ProjectCreationCache.invalidate(ProjectCreationCache.getUserPointsKey(userId));
    ProjectCreationCache.invalidate(ProjectCreationCache.getLinkedInAuthKey(userId));
  }

  /**
   * Invalidate all caches
   */
  static invalidateAllCaches(): void {
    ProjectCreationCache.clear();
  }

  /**
   * Get cache statistics for debugging
   */
  static getCacheStats(): { size: number; keys: string[] } {
    return ProjectCreationCache.getStats();
  }

  /**
   * Subscribe to real-time updates
   */
  static subscribeToUpdates(callback: () => void): () => void {
    const unsubscribeSettings = ProjectCreationRealtimeManager.subscribe('settings_changed', callback);
    const unsubscribePoints = ProjectCreationRealtimeManager.subscribe('user_points_changed', callback);
    
    // Return combined unsubscribe function
    return () => {
      unsubscribeSettings();
      unsubscribePoints();
    };
  }

  /**
   * Cleanup all subscriptions and caches
   */
  static cleanup(): void {
    ProjectCreationRealtimeManager.cleanup();
    ProjectCreationCache.clear();
  }
}